import { derived, get, writable, type Readable } from "svelte/store"
import type { BillingPlan, OrgBilling } from "@kontextwerk/control-web-client"
import { getAdminApi, getPublicApi } from "../../../../../api/kehrblatt/client"
import {
  type KehrblattPlanCapabilities,
  resolveKehrblattPlanCapabilities,
  resolveKehrblattPlanFamily,
} from "../org-billing-plan-capabilities"

export {
  type KehrblattPlanCapabilities,
  type KehrblattContactChannel,
  type KehrblattPlanFamily,
  planSupportsMobileMessage,
  planSupportsVoice,
  resolveKehrblattPlanCapabilities,
  resolveKehrblattPlanFamily,
} from "../org-billing-plan-capabilities"

export type OrgBillingPlanState = {
  billing: OrgBilling | null
  plan: BillingPlan | null
  loading: boolean
  error: string | null
  loadedAt: number | null
}

const initialState = (): OrgBillingPlanState => ({
  billing: null,
  plan: null,
  loading: false,
  error: null,
  loadedAt: null,
})

const normalizeOrgId = (orgId?: string | null) => (orgId || "").trim()
const normalizeIdentifier = (value?: string | null) => (value || "").trim().toLowerCase()

const planVersionStem = (value?: string | null) => {
  const normalized = normalizeIdentifier(value)

  if (!normalized) return ""

  return normalized.split("@")[0] || ""
}

const inflight = new Map<string, Promise<OrgBillingPlanState>>()
let inflightPublicPlans: Promise<BillingPlan[]> | null = null
const resolvedPlanVersionByOrg = new Map<string, string>()

export const orgBillingPlans = writable<Record<string, OrgBillingPlanState>>({})

orgBillingPlans.subscribe(($plans) => {
  for (const key of resolvedPlanVersionByOrg.keys()) {
    if (!(key in $plans)) resolvedPlanVersionByOrg.delete(key)
  }
})

const loadPublicBillingPlans = (): Promise<BillingPlan[]> => {
  if (inflightPublicPlans) return inflightPublicPlans

  const next = getPublicApi()
    .publicContent.app.listPlans()
    .finally(() => {
      if (inflightPublicPlans === next) inflightPublicPlans = null
    })

  inflightPublicPlans = next

  return next
}

const resolvePlanFromPublicCatalog = (
  billing: OrgBilling,
  plans: BillingPlan[],
): BillingPlan | null => {
  if (!Array.isArray(plans) || plans.length === 0) return null

  const rawVersionId = (billing.plan_version_id || "").trim()
  const normalizedVersionId = rawVersionId.toLowerCase()

  if (rawVersionId) {
    const exactMatch =
      plans.find((entry) => entry.id === rawVersionId) ??
      plans.find((entry) => normalizeIdentifier(entry.id) === normalizedVersionId)

    if (exactMatch) return exactMatch
  }

  const familyCandidate =
    normalizeIdentifier(billing.plan_family_id) ||
    (rawVersionId.includes("@") ? planVersionStem(rawVersionId) : "")

  if (familyCandidate) {
    const familyMatch = plans.find(
      (entry) =>
        normalizeIdentifier(entry.family_id) === familyCandidate ||
        normalizeIdentifier(entry.slug) === familyCandidate ||
        planVersionStem(entry.id) === familyCandidate,
    )

    if (familyMatch) return familyMatch
  }

  const canonicalFamily = resolveKehrblattPlanFamily({ billing, plan: null })

  if (canonicalFamily) {
    return (
      plans.find(
        (entry) => resolveKehrblattPlanFamily({ billing: null, plan: entry }) === canonicalFamily,
      ) ?? null
    )
  }

  return null
}

const isFreshEnough = (state: OrgBillingPlanState | null | undefined, maxAgeMs?: number) => {
  if (!state || state.loading || state.loadedAt === null) return false

  if (maxAgeMs === undefined || maxAgeMs === null) return true

  return Date.now() - state.loadedAt <= maxAgeMs
}

type LoadBillingPlanOptions = {
  force?: boolean
  includePlan?: boolean
  maxAgeMs?: number
}

const inflightKey = (orgId: string, includePlan?: boolean) =>
  `${orgId}:${includePlan ? "with-plan" : "billing"}`

export const refreshOrgBillingPlan = async (
  orgId?: string | null,
  options?: Pick<LoadBillingPlanOptions, "includePlan">,
): Promise<OrgBillingPlanState | null> => {
  const id = normalizeOrgId(orgId)

  if (!id) return null

  const includePlan = options?.includePlan === true
  const key = inflightKey(id, includePlan)

  const existing = inflight.get(key)

  if (existing) return existing

  const promise = (async () => {
    const previousEntry = get(orgBillingPlans)[id] ?? null
    orgBillingPlans.update((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? initialState()), loading: true, error: null },
    }))

    try {
      const billing = await getAdminApi().organization.billing.getOrgBilling(id)
      const sameVersionAsPrevious =
        Boolean(billing?.plan_version_id) &&
        previousEntry?.billing?.plan_version_id === billing?.plan_version_id
      let plan: BillingPlan | null =
        !includePlan && sameVersionAsPrevious ? (previousEntry?.plan ?? null) : null

      if (!includePlan && !sameVersionAsPrevious) {
        resolvedPlanVersionByOrg.delete(id)
      }

      if (includePlan && billing?.plan_version_id) {
        try {
          const plans = await loadPublicBillingPlans()
          plan = resolvePlanFromPublicCatalog(billing, plans)
          resolvedPlanVersionByOrg.set(id, billing.plan_version_id)
        } catch (err) {
          resolvedPlanVersionByOrg.delete(id)
          console.error("org_plan_fetch_failed", err)
        }
      }

      const state: OrgBillingPlanState = {
        billing,
        plan,
        loading: false,
        error: null,
        loadedAt: Date.now(),
      }
      orgBillingPlans.update((prev) => ({ ...prev, [id]: state }))

      return state
    } catch (err) {
      resolvedPlanVersionByOrg.delete(id)
      console.error("org_billing_fetch_failed", err)
      const state: OrgBillingPlanState = {
        ...initialState(),
        loading: false,
        error: "Abrechnungsplan konnte nicht geladen werden.",
      }
      orgBillingPlans.update((prev) => ({ ...prev, [id]: state }))

      return state
    } finally {
      inflight.delete(key)
    }
  })()

  inflight.set(key, promise)

  return promise
}

export const ensureOrgBillingPlan = async (
  orgId?: string | null,
  options?: Pick<LoadBillingPlanOptions, "includePlan" | "maxAgeMs">,
): Promise<OrgBillingPlanState | null> => {
  const id = normalizeOrgId(orgId)

  if (!id) return null

  const includePlan = options?.includePlan === true
  const key = inflightKey(id, includePlan)
  const existing = inflight.get(key)

  if (existing) return existing

  const cached = get(orgBillingPlans)[id]
  const hasResolvedPlan =
    Boolean(cached?.plan) ||
    (Boolean(cached?.billing?.plan_version_id) &&
      resolvedPlanVersionByOrg.get(id) === cached?.billing?.plan_version_id)

  if (
    isFreshEnough(cached, options?.maxAgeMs) &&
    (!includePlan || !cached?.billing?.plan_version_id || hasResolvedPlan)
  ) {
    return cached
  }

  return refreshOrgBillingPlan(id, { includePlan })
}

export const loadOrgBillingPlan = async (
  orgId?: string | null,
  options?: LoadBillingPlanOptions,
): Promise<OrgBillingPlanState | null> => {
  return options?.force
    ? refreshOrgBillingPlan(orgId, { includePlan: options.includePlan })
    : ensureOrgBillingPlan(orgId, {
        includePlan: options?.includePlan,
        maxAgeMs: options?.maxAgeMs,
      })
}

export const orgPlanLookup: Readable<(orgId?: string | null) => OrgBillingPlanState | null> =
  derived(orgBillingPlans, ($plans) => {
    return (orgId?: string | null) => $plans[normalizeOrgId(orgId)] ?? null
  })

export const orgVoicePermission: Readable<(orgId?: string | null) => boolean> = derived(
  orgBillingPlans,
  ($plans) => {
    return (orgId?: string | null) => {
      const entry = $plans[normalizeOrgId(orgId)]

      return resolveKehrblattPlanCapabilities(entry).hasPhone
    }
  },
)

export const orgContactChannelCapabilities: Readable<
  (orgId?: string | null) => KehrblattPlanCapabilities["allowedContactChannels"]
> = derived(orgBillingPlans, ($plans) => {
  return (orgId?: string | null) => {
    const entry = $plans[normalizeOrgId(orgId)]

    return resolveKehrblattPlanCapabilities(entry).allowedContactChannels
  }
})

export const orgPlanCapabilities: Readable<(orgId?: string | null) => KehrblattPlanCapabilities> =
  derived(orgBillingPlans, ($plans) => {
    return (orgId?: string | null) => {
      const entry = $plans[normalizeOrgId(orgId)]

      return resolveKehrblattPlanCapabilities(entry)
    }
  })
